/**
 * Plausible Analytics — typed event wrapper (IMT house template, ported from
 * Gamerhood S5 and adapted to HyperX Level Awakening).
 *
 * Two surfaces, both routed through PlausibleEventName so a typo on either is a
 * compile error:
 *   1. trackEvent(name, props?)          — programmatic; fire on a real signal
 *      (e.g. a successful register API response).
 *   2. plausibleEventClass(name, props?) — emits the
 *      `class="plausible-event-name=..."` string the tracker's tagged-events
 *      extension reads. Preferred for anchors/buttons: the class is inert HTML,
 *      so the click is captured even when an ad blocker suppresses the POST.
 *
 * To add an event: append it to PlausibleEventName, fire it, THEN register it as
 * a Goal in the Plausible dashboard (Settings → Goals → Custom event → paste the
 * exact name). See the client-integration event-catalog.md for the house baseline.
 *
 * UTM: never pass utm_* as a prop. Plausible joins each event to its visit's UTM
 * context server-side, so goals already split by source/medium/campaign under a
 * filter. Passing it as a prop double-stores it for zero analytic gain.
 */

// ===== ADAPT 1: the event union ============================================
// Only the events this site actually fires (so the operator's Goal list stays
// honest). Catalog events with no surface yet — Social Click, Video Play, File
// Download — are intentionally omitted; add them here when a surface ships,
// then register the matching Goal.
//
// The registration funnel reads end to end as:
//   Signup Step(email) → Signup Step(code) → Signup → Survey Complete
// Everything else is a standalone Goal, analyzed as a breakdown of its own
// props rather than as another funnel stage.
//
// Prop cardinality rule: every prop value below is drawn from a CLOSED set
// (a literal union, a slug, a bucket, or a 1-based position). Never pass a
// questionId, an email, a URL, or any other unbounded value — Plausible
// property breakdowns become unreadable and the storage cost is per value.
export type PlausibleEventName =
  // --- Registration funnel -------------------------------------------------
  // Per-step registration funnel. Fires on each successful step advance in
  // /sweepstakes with { step, durationBucket }. Read drop-off as a breakdown of
  // this event by `step`; per-step timing as `durationBucket` filtered to a step.
  // Steps used: 'email' (email accepted, OTP sent) → 'code' (OTP verified).
  | 'Signup Step'
  // Terminal registration conversion. Fires once on the successful register API
  // response (DetailsStep → handleRegistered, PENDING → ACTIVE) with
  // { durationBucket } for total flow time. Register as the primary Goal.
  | 'Signup'
  // A visitor who wanted to enter and couldn't, with a single { reason } prop:
  //   'already-entered'  — verified email is already ACTIVE (the HX-38
  //                        one-entry-per-email cutoff)
  //   'entries-not-open' — landed on /sweepstakes during PRE (AccessGate)
  //   'entries-closed'   — landed on /sweepstakes during POST (AccessGate)
  // None of these can ever reach `Signup`, so without this event they read as
  // ordinary funnel drop-off. Together they measure repeat-entry attempts and
  // entry demand outside the live window.
  | 'Signup Blocked'
  // --- Survey --------------------------------------------------------------
  // Per-question outcome inside the inline survey, with
  // { action: 'answer' | 'skip', position, durationBucket }. `position` is the
  // question's 1-based place in the served order (NOT its id — see the
  // cardinality rule above); `durationBucket` is cumulative from the moment the
  // survey step mounted. Read per-question skip rate as a breakdown by
  // `position` filtered to action=skip. Each question reports at most once per
  // session — a skip that the visitor later goes Back and answers upgrades to
  // 'answer' exactly once, and re-traversing an already-saved question is silent.
  | 'Survey Step'
  // Terminal survey event, always exactly one per registration, with
  // { outcome, position, answered, total, durationBucket }:
  //   'finished' — walked every question to the end (individual skips allowed)
  //   'skipped'  — bailed out via the Skip Survey link
  //   'none'     — the API served zero unanswered questions (nothing to show)
  // `position` is the question in play when the survey ended, which under
  // outcome='skipped' is the drop-off point; `answered`/`total` give the
  // completion ratio without needing to join back to the Survey Step events.
  // `durationBucket` here measures the SURVEY only, not the whole signup flow —
  // that is what `Signup`'s own durationBucket already covers.
  | 'Survey Complete'
  // --- Flow health ---------------------------------------------------------
  // A visitor-visible failure or validation block, with { step, reason }. This
  // is the flow's smoke alarm: a spike in reason='rate-limited' or
  // 'send-failed' during the broadcast is an incident, not a metric. Both props
  // are closed sets — see FormErrorStep / FormErrorReason below.
  | 'Form Error'
  // --- Engagement ----------------------------------------------------------
  // Primary CTA clicks, tagged on the anchor with { location } so they survive
  // ad-block POST suppression (doubling up on the automatic 'Outbound Link:
  // Click'). Locations:
  //   live-hero | watch-band   Twitch button, in-hero (LIVE) and body band
  //                            (PRE/POST)
  //   hero-replay | appbar-replay | gate-replay
  //                            the THREE separate paths to the POST replay —
  //                            hero heading, docked bar (post-scroll only),
  //                            and the closed-entries gate on /sweepstakes
  //   call-to-rise-watch       WATCH NOW at the FOOT of the live/post page
  //                            (HX-48). Kept distinct from the hero/bar
  //                            locations on purpose: it is the only watch CTA
  //                            a visitor reaches by reading the whole page, so
  //                            against `Section View`'s call-to-rise it gives
  //                            the read-to-click rate of the page's ending.
  //   enter-to-win             pre-tagged; the button is still unwired (HX-8)
  | 'CTA Click'
  // The HP gaming promo banner's "Shop Now" click (HX-42) — its OWN Goal, not a
  // `CTA Click` location, because it converts to a different place than every
  // other CTA on the page (the HP store, against a discount code) and the client
  // reads it as its own line item. { location, code }.
  | 'Promo Click'
  // Every secondary interaction that is neither a CTA nor a funnel step. One
  // Goal broken down by { target } (an area-prefixed slug) keeps the
  // dashboard's Goal list readable instead of scattering a dozen near-empty
  // goals across it. Current targets:
  //   footer-terms | footer-privacy | footer-cookies | footer-rules |
  //   footer-privacy-settings   (SiteFooter — all open in new tabs, so the
  //                              click is the ONLY link back to the footer)
  //   appbar-logo               (docked bar's back-to-top)
  //   reg-change-email | reg-resend-code   (OtpStep escape hatches; a resend
  //                              spike is a mail-deliverability signal)
  //   reg-back-home             (ThanksStep)
  //   gate-check-again          (AccessGate)
  //   bg-logo-home | bg-back-home  (/backgrounds — the header's HyperX mark
  //                              and the Back to Home button at the foot; the
  //                              page is unlisted, so these also show whether
  //                              anyone who lands there goes on to the site)
  //   privacy-analytics-on      (Privacy Settings opt-IN only — see the page
  //                              for why the opt-OUT is deliberately unreported)
  | 'UI Click'
  // Scroll reach on the home page — the only engagement signal a
  // scroll-and-watch landing page produces, since its body has almost no
  // clickable surface. Fires at most once per section per page view, when the
  // section's top passes 75% of the viewport (useSectionView). Sections, in
  // page order: prizes (LIVE/POST only) | hosts | promo-banner (only while the
  // HX-43 window is open) | heroes | call-to-rise.
  | 'Section View'
  // --- Downloads -----------------------------------------------------------
  // A wallpaper pulled from /backgrounds, with { wallpaper } = the art's slug
  // (corruption | dragons-lair | forest-edge — see content/wallpapers.ts, which
  // is the closed set this prop draws from). Its own Goal rather than a
  // `UI Click` target because the whole point of that page is which art people
  // actually want, and that only reads as a breakdown of one event. Tagged on
  // the anchor (plausibleEventClass) so a `download` navigation can't race the
  // POST — the browser starts the transfer the instant the click lands.
  | 'Wallpaper Download';
// Note: 'Outbound Link: Click' is fired automatically by the outbound-links
// script extension with a { url } prop; it is intentionally NOT in this union
// because we never fire it from code.
// ===========================================================================

/** Flow surface a `Form Error` came from — closed set, keeps the breakdown tight. */
export type FormErrorStep = 'email' | 'code' | 'details' | 'survey';

/** Why a `Form Error` fired — closed set. */
export type FormErrorReason =
  /** Client-side validation refused the submission before any request went out. */
  | 'invalid-email'
  | 'invalid-format'
  | 'missing-required'
  /** The API rejected the submitted value. */
  | 'invalid-code'
  | 'missing-text'
  /** 429 from the API — the OTP/registration rate limiter tripped. */
  | 'rate-limited'
  /** The request itself failed (5xx, network, unexpected shape). */
  | 'send-failed'
  | 'register-failed'
  | 'save-failed'
  | 'questions-load-failed';

type PlausiblePropValue = string | number | boolean;
type PlausibleQueueEntry = [string, { props?: Record<string, PlausiblePropValue> }?];
type PlausibleFn = ((
  name: string,
  options?: { props?: Record<string, PlausiblePropValue> },
) => void) & { q?: PlausibleQueueEntry[] };

declare global {
  interface Window {
    plausible?: PlausibleFn;
    __loadPlausible?: () => void;
    __unloadPlausible?: () => void;
  }
}

/**
 * Fire a Plausible custom event.
 *
 * Safe before the tracker loads (events queue via the index.html stub and
 * replay once script.js runs) and safe when Plausible is not loaded at all
 * (opt-out, ad-block, dev build without a domain) — it no-ops without throwing.
 *
 * Defense-in-depth: the index.html loader already refuses to inject the script
 * under GPC / plausible_ignore / la:consent-denied, so window.plausible is
 * usually the inert queue stub in those cases. This gate additionally covers the
 * edge where the script loaded (analytics granted at page load) and the user
 * then revoked via Privacy Settings mid-session.
 */
export function trackEvent(
  name: PlausibleEventName,
  props?: Record<string, PlausiblePropValue>,
): void {
  try {
    if (typeof window === 'undefined' || typeof window.plausible !== 'function') return;
    if (isAnalyticsDenied()) return;
    window.plausible(name, props ? { props } : undefined);
  } catch {
    /* analytics must never throw into the host app */
  }
}

/**
 * Build the className for declarative click-tracking via the tagged-events
 * extension:
 *   <a className={plausibleEventClass('CTA Click', { location: 'hero' })} ...>
 * Emits: "plausible-event-name=CTA+Click plausible-event-location=hero"
 * Combine with other classes via a template string.
 */
export function plausibleEventClass(
  name: PlausibleEventName,
  props?: Record<string, string>,
): string {
  const parts = [`plausible-event-name=${encodeForClass(name)}`];
  if (props) {
    for (const [key, value] of Object.entries(props)) {
      parts.push(`plausible-event-${key}=${encodeForClass(value)}`);
    }
  }
  return parts.join(' ');
}

function encodeForClass(s: string): string {
  return s.replace(/ /g, '+');
}

/**
 * Fire a `Form Error`. Thin helper over trackEvent purely so both props stay
 * inside their closed sets at the call site (a typo is a compile error).
 */
export function trackFormError(step: FormErrorStep, reason: FormErrorReason): void {
  trackEvent('Form Error', { step, reason });
}

/**
 * Bucket a raw duration (seconds) into a low-cardinality label. Use for the
 * `durationBucket` prop so timing stays analyzable without exploding the
 * property space.
 */
export function durationBucket(seconds: number): string {
  if (seconds < 10) return '<10s';
  if (seconds < 30) return '10-30s';
  if (seconds < 60) return '30-60s';
  if (seconds < 180) return '1-3m';
  if (seconds < 600) return '3-10m';
  return '>10m';
}

// ===== ADAPT 2: the consent gate ===========================================
// Mirrors the gate chain in index.html's PLAUSIBLE bootstrap. Keep the two in
// step. HyperX funnels both the analytics opt-out and GPC into Plausible's own
// `plausible_ignore` flag (see lib/consent.ts), so that is the load-bearing
// gate; the la:consent read is defense-in-depth. la:consent stores a FLAT
// { analytics: boolean } (NOT the house nested prefs.analytics).
const CONSENT_STORAGE_KEY = 'la:consent';
function isAnalyticsDenied(): boolean {
  try {
    if (
      (navigator as Navigator & { globalPrivacyControl?: boolean }).globalPrivacyControl === true
    ) {
      return true;
    }
  } catch {
    /* navigator blocked */
  }

  try {
    if (window.localStorage.getItem('plausible_ignore') === 'true') return true;
    const raw = window.localStorage.getItem(CONSENT_STORAGE_KEY);
    if (raw) {
      const saved = JSON.parse(raw) as { analytics?: unknown } | null;
      if (saved && saved.analytics === false) return true;
    }
  } catch {
    /* localStorage blocked or parse error */
  }

  return false;
}
// ===========================================================================
